import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, Camera, CheckCircle, ChevronDown, ChevronRight,
  ImagePlus, Loader2, Plus, RefreshCw, Trash2, User, Users, X,
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { labApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { AvatarCreatorForm, GENDER_OPTIONS, type AvatarDraft } from './lab-dashboard/AvatarCreatorForm';

// Biblioteka avatarów UGC: postaci generowane w HeyGen z promptu (konfigurator cech)
// + sceny (ta sama postać w innym otoczeniu, opcjonalnie z produktem w ręce).
// Gotowy avatar/scena jest wybieralny w produkcji reklam wideo (sekcja "Biblioteka").

interface AvatarScene {
  id: string;
  name: string;
  prompt: string | null;
  heygenLookId: string | null;
  previewUrl: string | null;
  referenceImageUrl: string | null;
  status: string;
  error: string | null;
  createdAt: string;
}

interface AvatarRecord {
  id: string;
  name: string;
  isSystem?: boolean;
  gender: string | null;
  ageRange: string | null;
  traits: Record<string, string> | null;
  prompt: string | null;
  previewUrl: string | null;
  status: string;
  error: string | null;
  defaultVoiceId?: string | null;
  defaultVoiceName?: string | null;
  createdAt: string;
  scenes: AvatarScene[];
}

interface AccountVoiceOption {
  voiceId: string;
  name: string;
}

const POLL_INTERVAL_MS = 8000;

function statusBadge(status: string, error?: string | null) {
  if (status === 'PENDING' || status === 'GENERATING') {
    return (
      <span className="flex items-center gap-1 text-[10px] font-medium text-warning">
        <Loader2 className="w-3 h-3 animate-spin" /> {status === 'PENDING' ? 'W kolejce...' : 'Generowanie...'}
      </span>
    );
  }
  if (status === 'FAILED') {
    return (
      <span className="flex items-center gap-1 text-[10px] font-medium text-red-400" title={error || undefined}>
        <AlertCircle className="w-3 h-3" /> Błąd
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-[10px] font-medium text-success">
      <CheckCircle className="w-3 h-3" /> Gotowy
    </span>
  );
}

export function LabAvatarsPage() {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [avatars, setAvatars] = useState<AvatarRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [sceneModalAvatar, setSceneModalAvatar] = useState<AvatarRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'avatar' | 'scene'; id: string; name: string } | null>(null);
  const [regenerateTarget, setRegenerateTarget] = useState<{ kind: 'avatar' | 'scene'; id: string; name: string } | null>(null);
  // Para postać+głos (audio-first): głosy z konta ElevenLabs; brak integracji = sekcja ukryta
  const [accountVoices, setAccountVoices] = useState<AccountVoiceOption[]>([]);
  const [savingVoiceFor, setSavingVoiceFor] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadAvatars = useCallback(async () => {
    try {
      const res = await labApi.getAvatars();
      setAvatars(res.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się pobrać biblioteki avatarów.');
    }
  }, []);

  useEffect(() => {
    void loadAvatars().finally(() => setLoading(false));
    // Głosy konta ElevenLabs do parowania z postacią (cicho - bez integracji
    // sekcja "Głos postaci" po prostu się nie renderuje)
    labApi.getVideoProductionOptions('elevenlabs')
      .then(res => setAccountVoices((res.data.data?.voices || []).map((voice: any) => ({ voiceId: voice.voiceId, name: voice.name }))))
      .catch(() => setAccountVoices([]));
  }, [loadAvatars]);

  const handleVoiceChange = async (avatar: AvatarRecord, voiceId: string) => {
    setSavingVoiceFor(avatar.id);
    try {
      await labApi.setAvatarVoice(avatar.id, {
        voiceId: voiceId || null,
        voiceName: accountVoices.find(voice => voice.voiceId === voiceId)?.name || null,
      });
      await loadAvatars();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się zapisać głosu postaci.');
    } finally {
      setSavingVoiceFor(null);
    }
  };

  const hasPending = avatars.some(avatar =>
    ['PENDING', 'GENERATING'].includes(avatar.status)
    || avatar.scenes.some(scene => ['PENDING', 'GENERATING'].includes(scene.status)),
  );

  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void loadAvatars(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPending, loadAvatars]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.kind === 'avatar') await labApi.deleteAvatar(deleteTarget.id);
      else await labApi.deleteAvatarScene(deleteTarget.id);
      setDeleteTarget(null);
      await loadAvatars();
    } catch {
      setDeleteTarget(null);
    }
  };

  const grouped = useMemo(() => {
    const groups = new Map<string, AvatarRecord[]>();
    for (const avatar of avatars) {
      const key = [
        GENDER_OPTIONS.find(item => item.value === avatar.gender)?.label || 'Inne',
        avatar.ageRange ? `${avatar.ageRange} lat` : '',
      ].filter(Boolean).join(' · ');
      const list = groups.get(key) || [];
      list.push(avatar);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [avatars]);

  return (
    <div className="space-y-6">
      <div className="animate-fade-in flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/marketing/laboratorium" className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Laboratorium
          </Link>
          <h1 className="text-3xl font-bold text-text-primary">Avatary UGC</h1>
          <p className="text-text-secondary mt-2">
            Biblioteka postaci do reklam wideo: ChatGPT generuje portret z cech, dodajesz sceny (inne otoczenie, produkt w ręce), a gotowe ujęcia wybierasz w edytorze ujęć i produkcji HeyGen.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-accent-primary/15 border border-accent-primary/30 rounded-lg text-accent-primary text-sm font-medium hover:bg-accent-primary/25 transition-colors self-start"
        >
          <Plus className="w-4 h-4" /> Stwórz avatara
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-sm text-danger">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-sm text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Ładowanie biblioteki...
        </div>
      ) : avatars.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Biblioteka avatarów jest pusta"
          description="Stwórz pierwszego avatara: wyklikaj cechy postaci, a HeyGen wygeneruje fotorealistyczne zdjęcie bazowe. Potem dodasz sceny i użyjesz go w produkcji reklam."
          buttonLabel="Stwórz avatara"
          onButtonClick={() => setCreateOpen(true)}
          buttonIcon={Plus}
        />
      ) : (
        <div className="space-y-6">
          {grouped.map(([groupLabel, groupAvatars]) => (
            <div key={groupLabel}>
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">{groupLabel}</h2>
              <div className="space-y-3">
                {groupAvatars.map(avatar => {
                  const isExpanded = expandedId === avatar.id;
                  return (
                    <div key={avatar.id} className="bg-surface-raised rounded-xl border border-border overflow-hidden">
                      <div className="flex items-center gap-3 p-3">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : avatar.id)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                        >
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-text-muted shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-text-muted shrink-0" />}
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface-hover border border-border-subtle shrink-0">
                            {avatar.previewUrl ? (
                              <img src={avatar.previewUrl} alt={avatar.name} loading="lazy" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="w-5 h-5 text-text-disabled" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-text-primary truncate">{avatar.name}</span>
                              {avatar.isSystem && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-accent-info/15 text-accent-info border border-accent-info/20 shrink-0">
                                  Katalog
                                </span>
                              )}
                              {statusBadge(avatar.status, avatar.error)}
                            </div>
                            <span className="text-[11px] text-text-muted block truncate">
                              {avatar.scenes.length > 0 ? `Sceny: ${avatar.scenes.length}` : 'Brak scen'}
                            </span>
                          </div>
                        </button>
                        {avatar.status === 'FAILED' && (!avatar.isSystem || isSuperAdmin) && (
                          <button
                            onClick={() => labApi.retryAvatar(avatar.id).then(loadAvatars)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-warning bg-warning/10 border border-warning/20 rounded-lg hover:bg-warning/15 transition-all"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Ponów
                          </button>
                        )}
                        {(!avatar.isSystem || isSuperAdmin) && (
                          <button
                            onClick={() => setDeleteTarget({ kind: 'avatar', id: avatar.id, name: avatar.name })}
                            className="p-1.5 text-text-muted hover:text-red-400 transition-colors"
                            title="Usuń avatara"
                            aria-label="Usuń avatara"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-border-subtle pt-3 space-y-3">
                          {avatar.status === 'FAILED' && avatar.error && (
                            <p className="text-[11px] text-red-400">{avatar.error}</p>
                          )}
                          {avatar.prompt && (
                            <p className="text-[11px] text-text-muted bg-surface-hover/40 rounded-lg border border-border-subtle p-2.5">{avatar.prompt}</p>
                          )}
                          {/* Para postać+głos: jeden głos ElevenLabs = spójny dźwięk
                              całej kreacji (hook + sceny). Tylko własne postaci. */}
                          {!avatar.isSystem && accountVoices.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-text-primary">Głos postaci</span>
                              <div className="w-56">
                                <Select
                                  value={avatar.defaultVoiceId || ''}
                                  onChange={e => void handleVoiceChange(avatar, e.target.value)}
                                  disabled={savingVoiceFor === avatar.id}
                                >
                                  <option value="">Bez przypisania</option>
                                  {accountVoices.map(voice => (
                                    <option key={voice.voiceId} value={voice.voiceId}>{voice.name}</option>
                                  ))}
                                </Select>
                              </div>
                              {savingVoiceFor === avatar.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-warning" />}
                              <span className="text-[10px] text-text-muted">
                                jeden głos = ta sama barwa w hooku i wszystkich scenach
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-text-primary">Sceny</span>
                            <button
                              onClick={() => setSceneModalAvatar(avatar)}
                              disabled={avatar.status !== 'READY' || (avatar.isSystem && !isSuperAdmin)}
                              title={avatar.status !== 'READY' ? 'Avatar bazowy musi być najpierw wygenerowany' : avatar.isSystem && !isSuperAdmin ? 'Sceny avatara z katalogu systemowego dodaje superadmin' : undefined}
                              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-accent-info bg-info/10 border border-accent-info/20 rounded-lg hover:bg-info/15 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Camera className="w-3 h-3" /> Dodaj scenę
                            </button>
                            {avatar.status === 'READY' && (!avatar.isSystem || isSuperAdmin) && (
                              <button
                                onClick={() => setRegenerateTarget({ kind: 'avatar', id: avatar.id, name: avatar.name })}
                                disabled={avatar.scenes.length > 0}
                                title={avatar.scenes.length > 0 ? 'Usuń sceny przed regeneracją postaci' : undefined}
                                className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-text-secondary bg-surface-hover/40 border border-border rounded-lg hover:border-border-strong hover:text-text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <RefreshCw className="w-3 h-3" /> Regeneruj postać
                              </button>
                            )}
                          </div>
                          {avatar.scenes.length === 0 ? (
                            <p className="text-[11px] text-text-disabled">
                              Brak scen - avatar występuje tylko w ujęciu bazowym. Dodaj sceny (inne otoczenie, z produktem w ręce), żeby różnicować ujęcia w reklamach.
                            </p>
                          ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                              {avatar.scenes.map(scene => (
                                <div key={scene.id} className="rounded-lg border border-border-subtle overflow-hidden bg-surface-hover/30 group relative">
                                  <div className="aspect-square bg-surface-hover">
                                    {scene.previewUrl ? (
                                      <img src={scene.previewUrl} alt={scene.name} loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        {['PENDING', 'GENERATING'].includes(scene.status)
                                          ? <Loader2 className="w-5 h-5 animate-spin text-warning" />
                                          : <Camera className="w-5 h-5 text-text-disabled" />}
                                      </div>
                                    )}
                                  </div>
                                  <div className="px-2 py-1.5">
                                    <span className="text-[10px] font-medium text-text-secondary block truncate">{scene.name}</span>
                                    {statusBadge(scene.status, scene.error)}
                                  </div>
                                  <div className={`absolute top-1 right-1 flex gap-1 transition-opacity ${avatar.isSystem && !isSuperAdmin ? 'hidden' : 'opacity-0 group-hover:opacity-100'}`}>
                                    {scene.status === 'FAILED' && (
                                      <button
                                        onClick={() => labApi.retryAvatarScene(scene.id).then(loadAvatars)}
                                        className="p-1 rounded-md bg-black/60 text-warning hover:text-white transition-colors"
                                        title="Ponów generowanie"
                                        aria-label="Ponów generowanie sceny"
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                      </button>
                                    )}
                                    {scene.status === 'READY' && (
                                      <button
                                        onClick={() => setRegenerateTarget({ kind: 'scene', id: scene.id, name: scene.name })}
                                        className="p-1 rounded-md bg-black/60 text-text-secondary hover:text-white transition-colors"
                                        title="Regeneruj scenę"
                                        aria-label="Regeneruj scenę"
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => setDeleteTarget({ kind: 'scene', id: scene.id, name: scene.name })}
                                      className="p-1 rounded-md bg-black/60 text-text-secondary hover:text-red-400 transition-colors"
                                      title="Usuń scenę"
                                      aria-label="Usuń scenę"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {createOpen && (
        <CreateAvatarModal
          showSystemToggle={isSuperAdmin}
          onCreated={async () => {
            setCreateOpen(false);
            await loadAvatars();
          }}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {sceneModalAvatar && (
        <CreateSceneModal
          avatar={sceneModalAvatar}
          onCreated={async () => {
            setSceneModalAvatar(null);
            await loadAvatars();
          }}
          onClose={() => setSceneModalAvatar(null)}
        />
      )}

      {regenerateTarget && (
        <RegenerateModal
          target={regenerateTarget}
          onDone={async () => {
            setRegenerateTarget(null);
            await loadAvatars();
          }}
          onClose={() => setRegenerateTarget(null)}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title={deleteTarget?.kind === 'avatar' ? 'Usunąć avatara?' : 'Usunąć scenę?'}
        message={deleteTarget ? <>Czy na pewno chcesz usunąć <strong>{deleteTarget.name}</strong>?{deleteTarget.kind === 'avatar' ? ' Usunięte zostaną też wszystkie sceny tego avatara.' : ''}</> : ''}
        confirmLabel="Usuń"
        danger
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ============================================
// Regeneracja avatara próbnego / sceny z opcjonalną poprawką
// ============================================

interface RegenerateModalProps {
  target: { kind: 'avatar' | 'scene'; id: string; name: string };
  onDone: () => Promise<void>;
  onClose: () => void;
}

function RegenerateModal({ target, onDone, onClose }: RegenerateModalProps) {
  const [instruction, setInstruction] = useState('');
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleRegenerate = async () => {
    setModalError(null);
    setStarting(true);
    try {
      if (target.kind === 'avatar') await labApi.regenerateAvatar(target.id, instruction.trim() || undefined);
      else await labApi.regenerateAvatarScene(target.id, instruction.trim() || undefined);
      await onDone();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się uruchomić regeneracji.');
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-accent-primary" /> Regeneruj: {target.name}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">
          {target.kind === 'avatar'
            ? 'Nowa generacja postaci na bazie dotychczasowego opisu. Możesz wskazać co poprawić.'
            : 'Nowa generacja tej sceny (ta sama postać). Możesz wskazać co poprawić.'}
        </p>

        <label htmlFor="regenerate-instruction" className="text-xs font-medium text-text-secondary mb-1.5 block">
          Co poprawić? (opcjonalnie)
        </label>
        <textarea
          id="regenerate-instruction"
          value={instruction}
          onChange={e => setInstruction(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="np. younger face, warmer smile, less makeup..."
          className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
        />

        {modalError && (
          <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleRegenerate}
            disabled={starting}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Regeneruj
          </button>
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Konfigurator avatara: modal wokół współdzielonego AvatarCreatorForm
// ============================================

interface CreateAvatarModalProps {
  showSystemToggle: boolean;
  onCreated: () => Promise<void>;
  onClose: () => void;
}

function CreateAvatarModal({ showSystemToggle, onCreated, onClose }: CreateAvatarModalProps) {
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCreate = async (draft: AvatarDraft) => {
    setModalError(null);
    setStarting(true);
    try {
      await labApi.createAvatar(draft);
      await onCreated();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się utworzyć avatara.');
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Users className="w-4 h-4 text-accent-primary" /> Nowy avatar
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">
          Wyklikaj cechy postaci - prompt złoży się automatycznie (możesz go dopracować ręcznie). Portret generuje ChatGPT (gpt-image), a HeyGen dostaje gotowe zdjęcie. Generowanie zajmuje 1-2 minuty i dzieje się w tle. Reguły naturalności UGC (jakość zdjęcia z telefonu, bez retuszu) dokładamy automatycznie.
        </p>
        <AvatarCreatorForm
          submitting={starting}
          submitLabel="Generuj avatara"
          showSystemToggle={showSystemToggle}
          error={modalError}
          onSubmit={handleCreate}
        />
      </div>
    </div>
  );
}

// ============================================
// Nowa scena avatara: otoczenie/ujęcie + opcjonalne zdjęcie produktu
// ============================================

interface CreateSceneModalProps {
  avatar: AvatarRecord;
  onCreated: () => Promise<void>;
  onClose: () => void;
}

function CreateSceneModal({ avatar, onCreated, onClose }: CreateSceneModalProps) {
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleUpload = async (file: File) => {
    setModalError(null);
    setUploading(true);
    try {
      const res = await labApi.uploadAvatarReference(file);
      setReferenceImageUrl(res.data.data?.url || null);
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się wgrać zdjęcia.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    setModalError(null);
    setStarting(true);
    try {
      await labApi.createAvatarScene(avatar.id, {
        name: name.trim(),
        prompt: prompt.trim(),
        referenceImageUrl,
      });
      await onCreated();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się utworzyć sceny.');
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Camera className="w-4 h-4 text-accent-primary" /> Nowa scena: {avatar.name}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">
          Ta sama postać w nowym ujęciu - opisz otoczenie, strój lub kadr. Możesz dodać zdjęcie produktu jako referencję (avatar z produktem w ręce). Reguły naturalności UGC dokładamy automatycznie.
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="scene-name" className="text-xs font-medium text-text-secondary mb-1.5 block">Nazwa sceny</label>
            <input
              id="scene-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='np. "Kuchnia z produktem"'
              className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
            />
          </div>

          <div>
            <label htmlFor="scene-prompt" className="text-xs font-medium text-text-secondary mb-1.5 block">
              Opis sceny (po angielsku dla najlepszych rezultatów)
            </label>
            <textarea
              id="scene-prompt"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="np. standing in a bright kitchen, holding the product in her right hand, showing it to the camera"
              className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
            />
          </div>

          <div>
            <span className="text-xs font-medium text-text-secondary mb-1.5 block">Zdjęcie produktu (opcjonalnie)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
                e.target.value = '';
              }}
            />
            {referenceImageUrl ? (
              <div className="flex items-center gap-3">
                <img src={referenceImageUrl} alt="Zdjęcie referencyjne" className="w-16 h-16 rounded-lg object-cover border border-border-subtle" />
                <button
                  onClick={() => setReferenceImageUrl(null)}
                  className="text-[11px] text-text-muted hover:text-red-400 transition-colors"
                >
                  Usuń zdjęcie
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary bg-surface-hover border border-border rounded-lg hover:border-accent-primary/30 hover:text-text-primary transition-all disabled:opacity-40"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                Wgraj zdjęcie produktu
              </button>
            )}
            <p className="text-[10px] text-text-disabled mt-1.5">
              HeyGen użyje zdjęcia jako referencji - wspomnij o produkcie także w opisie sceny (np. "holding the product").
            </p>
          </div>
        </div>

        {modalError && (
          <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleCreate}
            disabled={starting || uploading || !name.trim() || prompt.trim().length < 10}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            Generuj scenę
          </button>
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
